import React, { useState, useRef, useEffect } from 'react';
import { useLang, useTheme, usePlayerSearch } from '../App';
import Profile from './Profile';

/* ══════════════════════════════════════════
   NAV ITEMS
══════════════════════════════════════════ */
const getNavItems = (t, lang) => [
  { id: "dashboard", icon: "⊞",  label: t.nav.dashboard       },
  { id: "profile",   icon: "◉",  label: t.nav.profile         },
  { id: "stats",     icon: "◈",  label: t.nav.stats           },
  { id: "maps",      icon: "♪",  label: t.nav.maps            },
  { id: "search",    icon: "◎", label: lang === 'ru' ? "Поиск" : "Search" },
  { id: "devs",      icon: "⌬",  label: "Devs"                },
];

/* ══════════════════════════════════════════
   THEME META
══════════════════════════════════════════ */
const THEME_GRADIENTS = {
  midnight: 'linear-gradient(135deg,#ff6eb4,#d44f8e)',
  obsidian: 'linear-gradient(135deg,#ba8cff,#8b5cf6)',
  ember:    'linear-gradient(135deg,#ff96aa,#e05575)',
  dark:     'linear-gradient(135deg,#555,#222)',
  ocean:    'linear-gradient(135deg,#4fc3f7,#0288d1)',
};
const THEME_LABELS = {
  midnight: { en:'Sakura Night',   ru:'Сакура Ночь'  },
  obsidian: { en:'Lavender Dream', ru:'Лаванда'      },
  ember:    { en:'Peach Blossom',  ru:'Персик'       },
  dark:     { en:'Pure Dark',      ru:'Тёмная'       },
  ocean:    { en:'Deep Ocean',     ru:'Океан'        },
};

/* ══════════════════════════════════════════
   SETTINGS PANEL
══════════════════════════════════════════ */
const SettingsPanel = ({ onClose, anchorRef, petals, setPetals }) => {
  const { lang, setLang }   = useLang();
  const { themeKey, setTheme } = useTheme();
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={panelRef} style={{
      position:'fixed', left:84, bottom:20, zIndex:600,
      width:256, background:'var(--card2)', border:'1px solid var(--border)',
      borderRadius:20, padding:'18px 16px',
      boxShadow:'0 12px 48px rgba(0,0,0,0.6)',
      animation:'fadeUp 0.22s cubic-bezier(.34,1.4,.64,1) both',
    }}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <span style={{fontWeight:800,fontSize:13,color:'var(--a3)'}}>
          {lang==='ru' ? '⚙ Настройки' : '⚙ Settings'}
        </span>
        <button onClick={onClose} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:18,lineHeight:1,padding:'0 4px'}}>×</button>
      </div>

      {/* Language */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:10,color:'var(--muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,fontWeight:700}}>
          {lang==='ru' ? 'Язык' : 'Language'}
        </div>
        <div style={{display:'flex',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
          {['en','ru'].map(l=>(
            <button key={l} onClick={()=>setLang(l)} style={{
              flex:1, padding:'7px 0', fontSize:12, fontWeight:800,
              background: lang===l ? 'var(--a)' : 'transparent',
              color: lang===l ? 'white' : 'var(--muted)',
              border:'none', cursor:'pointer', fontFamily:'inherit', transition:'all 0.2s',
            }}>{l.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {/* Theme */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:10,color:'var(--muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,fontWeight:700}}>
          {lang==='ru' ? 'Тема' : 'Theme'}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:5}}>
          {Object.entries(THEME_LABELS).map(([k,labels])=>{
            const active = themeKey===k;
            return (
              <button key={k} onClick={()=>setTheme(k)} style={{
                display:'flex',alignItems:'center',gap:10,
                padding:'7px 11px', borderRadius:11, border:'1px solid',
                borderColor: active ? 'var(--a)' : 'var(--border)',
                background: active ? 'var(--dim)' : 'transparent',
                cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
              }}>
                <div style={{width:18,height:18,borderRadius:6,flexShrink:0,background:THEME_GRADIENTS[k]}}/>
                <span style={{fontSize:12,fontWeight:active?700:500,color:active?'var(--a3)':'var(--muted)'}}>
                  {lang==='ru' ? labels.ru : labels.en}
                </span>
                {active && <span style={{marginLeft:'auto',color:'var(--a)',fontSize:11}}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Particles */}
      <div>
        <div style={{fontSize:10,color:'var(--muted)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,fontWeight:700}}>
          {lang==='ru' ? 'Анимация' : 'Animation'}
        </div>
        <button onClick={()=>setPetals(v=>!v)} style={{
          display:'flex',alignItems:'center',gap:10,
          width:'100%', padding:'7px 11px', borderRadius:11, border:'1px solid',
          borderColor: petals ? 'var(--a)' : 'var(--border)',
          background: petals ? 'var(--dim)' : 'transparent',
          cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
        }}>
          <span style={{fontSize:16}}>🌸</span>
          <span style={{fontSize:12,fontWeight:600,color:petals?'var(--a3)':'var(--muted)'}}>
            {lang==='ru' ? 'Частицы' : 'Particles'}
          </span>
          <div style={{marginLeft:'auto',width:30,height:16,borderRadius:8,background:petals?'var(--a)':'var(--border)',position:'relative',transition:'background 0.2s',flexShrink:0}}>
            <div style={{position:'absolute',top:2,left:petals?13:2,width:12,height:12,borderRadius:'50%',background:'white',transition:'left 0.2s',boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}/>
          </div>
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   SEARCH PAGE
   Ключевые фиксы:
   1. selectedPlayer — локальный state, профиль показывается прямо здесь
   2. НЕ вызываем onViewProfile (он менял page в App → убивал state)
══════════════════════════════════════════ */
export const SearchPage = ({ onViewProfile }) => {
  const { lang }   = useLang();

  const [query,           setQuery]           = useState('');
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [result,          setResult]          = useState(null);
  const [selectedPlayer,  setSelectedPlayer]  = useState(null);
  const [profileLoading,  setProfileLoading]  = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const countryFlag = (code) => {
    if (!code || code.length !== 2) return '🏳️';
    try { return code.toUpperCase().replace(/./g, c => String.fromCodePoint(c.charCodeAt(0)+127397)); }
    catch { return '🏳️'; }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(null); setResult(null); setSelectedPlayer(null);
    try {
      const res  = await fetch('/api/search-user', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ username: query.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'User not found');
      // Подтягиваем custom_banner из нашей базы
      try {
        const profileRes = await fetch(`/api/get-profile?osu_id=${data.id}`);
        const profileData = await profileRes.json();
        if (profileRes.ok && profileData.custom_banner) {
          data.profileCustomBanner = profileData.custom_banner;
        }
      } catch {}
      setResult(data);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Открыть инлайн-профиль — НЕ меняем page в App
const handleOpenProfile = async (player) => {
  setProfileLoading(true);
  setSelectedPlayer(player); // нужен для кнопки "назад" и заголовка
  try {
    const res  = await fetch(`/api/get-profile?osu_id=${player.id}`);
    const data = await res.json();
    if (res.ok && data.registered) {
      setSelectedPlayer({
        ...player,
        profileStatus:   data.status,
        profileSocials:  data.socials         || {},
        profileWidgets:  data.profile_widgets || ['topplay','stats','skills'],
        profileColor:    data.profile_color   || null,
        profileFavMap:        data.fav_map         || null,
        profileSetup:         data.setup           || {},
        trackerJoinedAt:      data.joined_at       || null,
        profileCustomBanner:  data.custom_banner   || null,
      });
    }
  } catch {}
  finally { setProfileLoading(false); }
};

  // ── Показываем Profile прямо здесь ────────────────────────────
  if (selectedPlayer) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <button
          onClick={() => setSelectedPlayer(null)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 18px', borderRadius: 20, cursor: 'pointer',
            background: 'var(--bg2)', border: '1px solid var(--border)',
            color: 'var(--muted)', fontSize: 13, fontFamily: 'inherit',
            width: 'fit-content',
          }}
        >
          ← {lang === 'ru' ? 'Назад к поиску' : 'Back to search'}
          <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--dim)', border: '1px solid var(--border)', color: 'var(--a)' }}>
            {selectedPlayer.username}
          </span>
        </button>

        {profileLoading ? (
          /* ── Скелетон пока грузится get-profile ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Hero skeleton */}
            <div className="card" style={{ padding: 24, overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ width: 76, height: 76, borderRadius: 20, background: 'var(--bg2)', flexShrink: 0, animation: 'softPulse 1.5s ease infinite' }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ height: 22, width: '40%', borderRadius: 8, background: 'var(--bg2)', animation: 'softPulse 1.5s ease infinite' }} />
                  <div style={{ height: 14, width: '25%', borderRadius: 6, background: 'var(--bg2)', animation: 'softPulse 1.5s ease infinite 0.1s' }} />
                  <div style={{ height: 12, width: '35%', borderRadius: 6, background: 'var(--bg2)', animation: 'softPulse 1.5s ease infinite 0.2s' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1,2,3].map(i => (
                  <div key={i} style={{ flex: 1, height: 52, borderRadius: 10, background: 'var(--bg2)', animation: `softPulse 1.5s ease infinite ${i * 0.1}s` }} />
                ))}
              </div>
            </div>
            {/* Widgets skeleton */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[2,1,1,2,4].map((cols, i) => (
                <div key={i} style={{ gridColumn: `span ${cols}`, height: cols === 4 ? 160 : 120, borderRadius: 22, background: 'var(--card)', border: '1px solid var(--border)', animation: `softPulse 1.5s ease infinite ${i * 0.08}s` }} />
              ))}
            </div>
          </div>
        ) : (
          <Profile user={selectedPlayer} isReadOnly={true} />
        )}
      </div>
    );
  }

  // ── Обычный поиск ─────────────────────────────────────────────
  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      <div className="fu">
        <h2 className="syne" style={{fontSize:22,fontWeight:800,marginBottom:6}}>
          ◎ {lang==='ru' ? 'Поиск игрока' : 'Search Player'}
        </h2>
        <p style={{fontSize:13,color:'var(--muted)'}}>
          {lang==='ru'
            ? 'Найди любого игрока osu! по никнейму'
            : 'Find any osu! player by username'}
        </p>
      </div>

      <div className="card fu d1" style={{padding:24,position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,background:'radial-gradient(circle at 80% 50%,var(--dim),transparent 60%)',pointerEvents:'none'}}/>
        <div style={{display:'flex',gap:10,position:'relative'}}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key==='Enter' && handleSearch()}
            placeholder={lang==='ru' ? 'Никнейм игрока...' : 'Player username...'}
            style={{flex:1,padding:'11px 16px',fontSize:14,borderRadius:14}}
          />
          <button className="btn-accent" style={{padding:'11px 24px',fontSize:14,borderRadius:14,whiteSpace:'nowrap'}} onClick={handleSearch} disabled={loading}>
            {loading
              ? <span style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
                  {lang==='ru' ? 'Ищу...' : 'Searching...'}
                </span>
              : (lang==='ru' ? '◎ Найти' : '◎ Search')}
          </button>
        </div>
        {error && (
          <div style={{marginTop:14,padding:'10px 14px',borderRadius:12,fontSize:13,background:'rgba(248,113,113,0.1)',border:'1px solid rgba(248,113,113,0.3)',color:'#f87171'}}>
            ❌ {error}
          </div>
        )}
      </div>

      {result && (
        <div className="card fu d2" style={{overflow:'hidden'}}>
          <div style={{
            height:80,
            background: (() => {
              const bannerUrl = (() => {
                if (!result.profileCustomBanner) return null;
                try { const p = typeof result.profileCustomBanner === 'object' ? result.profileCustomBanner : JSON.parse(result.profileCustomBanner); return p.url || null; }
                catch { return result.profileCustomBanner; }
              })();
              const url = bannerUrl || result.cover_url;
              return url ? `url(${url}) center ${(() => { try { const p = JSON.parse(result.profileCustomBanner||'{}'); return `${p.posY??30}%`; } catch { return '30%'; } })()} / cover` : 'linear-gradient(135deg,var(--dim),var(--bg2))';
            })(),
            position:'relative',
          }}>
            <div style={{position:'absolute',inset:0,background:'linear-gradient(to bottom,transparent 30%,var(--card) 100%)'}}/>
          </div>

          <div style={{padding:'0 24px 24px',marginTop:-32,position:'relative'}}>
            <div style={{display:'flex',alignItems:'flex-end',gap:16,marginBottom:16}}>
              <div style={{
                width:72,height:72,borderRadius:18,flexShrink:0,
                background: result.avatar_url ? `url(${result.avatar_url}) center/cover` : 'var(--grad)',
                border:'3px solid var(--card)', boxShadow:'0 6px 24px rgba(0,0,0,0.4)',
                overflow:'hidden', display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:28,color:'white',fontWeight:800,
              }}>
                {!result.avatar_url && result.username?.[0]?.toUpperCase()}
              </div>

              <div style={{flex:1,paddingBottom:4}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:4}}>
                  <span className="syne" style={{fontSize:22,fontWeight:800}}>{result.username}</span>
                  <span style={{fontSize:20}}>{countryFlag(result.country_code)}</span>
                  <span style={{
                    display:'flex',alignItems:'center',gap:5,
                    padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                    background: result.is_online ? 'rgba(74,222,128,0.1)' : 'rgba(255,255,255,0.05)',
                    border:`1px solid ${result.is_online ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)'}`,
                    color: result.is_online ? '#4ade80' : 'var(--muted)',
                  }}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:result.is_online?'#4ade80':'#555',display:'inline-block',boxShadow:result.is_online?'0 0 6px rgba(74,222,128,0.7)':'none'}}/>
                    {result.is_online ? (lang==='ru'?'Онлайн':'Online') : (lang==='ru'?'Офлайн':'Offline')}
                  </span>
                </div>
                <div style={{fontSize:12,color:'var(--muted)'}}>
                  {lang==='ru'?'В osu! с':'osu! since'} <b style={{color:'var(--text)'}}>{result.joined}</b>
                  {' · '}{(result.play_count||0).toLocaleString()} {lang==='ru'?'игр':'plays'}
                </div>
              </div>

              <div style={{textAlign:'right',paddingBottom:4}}>
                <div className="mono" style={{fontSize:28,fontWeight:700,color:'var(--a)',lineHeight:1}}>{(Math.round(result.pp||0)).toLocaleString()}</div>
                <div style={{fontSize:11,color:'var(--muted)'}}>pp</div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
              {[
                {l:lang==='ru'?'Глоб. рейтинг':'Global Rank', v:`#${(result.global_rank||0).toLocaleString()}`, c:'var(--a)'},
                {l:lang==='ru'?'Рейтинг страны':'Country Rank',v:`#${(result.country_rank||0).toLocaleString()}`,c:'var(--amber)'},
                {l:lang==='ru'?'Точность':'Accuracy',          v:`${result.accuracy||0}%`,                      c:'#68d891'},
              ].map(s=>(
                <div key={s.l} style={{textAlign:'center',padding:'10px',background:'var(--bg2)',borderRadius:12,border:'1px solid var(--border)'}}>
                  <div className="mono" style={{fontSize:16,fontWeight:700,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>{s.l}</div>
                </div>
              ))}
            </div>

            <div style={{display:'flex',gap:10}}>
              <button className="btn-accent" style={{flex:1,padding:'11px',fontSize:13,borderRadius:12}}
                onClick={() => handleOpenProfile(result)}>
                {lang==='ru' ? '👁 Открыть профиль' : '👁 View Full Profile'}
              </button>
              <button className="btn-ghost" style={{padding:'11px 18px',fontSize:13,borderRadius:12}}
                onClick={()=>window.open(`https://osu.ppy.sh/users/${result.id}`,'_blank')}>
                osu! ↗
              </button>
            </div>
          </div>
        </div>
      )}

      {!result && !error && !loading && (
        <div className="card fu d3" style={{padding:48,textAlign:'center'}}>
          <div style={{fontSize:48,marginBottom:16,opacity:0.4}}>◎</div>
          <div style={{fontWeight:700,marginBottom:8,fontSize:15}}>{lang==='ru'?'Поиск по базе osu!':'Search the osu! database'}</div>
          <div style={{fontSize:13,color:'var(--muted)',lineHeight:1.7}}>
            {lang==='ru'?'Введи никнейм игрока.':'Type any player username.'}
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════
   DEVS PAGE
══════════════════════════════════════════ */
export const DevsPage = () => {
  const { lang } = useLang();
  const [copied, setCopied] = useState(null);

  const devs = [
    { username:'mpahu',      role:lang==='ru'?'Основатель · Backend · Frontend':'Founder · Backend · Frontend', avatar:'https://i.pinimg.com/736x/98/0a/fd/980afd42e6771d44e98db514e3cca856.jpg', osuUrl:'https://osu.ppy.sh/users/mpahu',        flag:'🇷🇺', badge:lang==='ru'?'Главный':'Lead',     badgeColor:'#ff6eb4' },
    { username:'mykachitos', role:lang==='ru'?'Разработчик · Идеи':'Developer · Vision',                       avatar:'https://i.pinimg.com/736x/f2/ff/ed/f2ffed59631a751e64fbadd55ce20b2b.jpg', osuUrl:'https://osu.ppy.sh/users/15858182',    flag:'🇷🇺', badge:'Dev',                           badgeColor:'#7eb8f7' },
    { username:'ognev__',    role:lang==='ru'?'Стример · Поддержка':'Streamer · Support',                      avatar:'https://i.pinimg.com/736x/1b/72/78/1b7278c9b8047174f7e46005c4c64fd0.jpg', twitchUrl:'https://twitch.tv/ognev__',         flag:'🇷🇺', badge:lang==='ru'?'Саппортер':'Supporter', badgeColor:'#9147ff' },
  ];

  const crypto = [
    { name:'BTC',           address:'bc1q7acacl4zvddvmgcr64z2pj6n63hu5jaqnvejgz'  },
    { name:'ETH',           address:'0xd20ae9aa7c2660d0c7a0aca84f744805ec8b12db'  },
    { name:'USDT (TRC20)',  address:'TAu8atVEqWM867mDdtZiKw9bRqTxBEkePD'          },
    { name:'TRX',           address:'TAu8atVEqWM867mDdtZiKw9bRqTxBEkePD'          },
    { name:'Litecoin',      address:'ltc1qdjsf7v9my5p5l32yrl8snp33xeadt0yrzyaky8' },
  ];

  const copyToClipboard = (text, name) => {
    navigator.clipboard.writeText(text); setCopied(name);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24,maxWidth:1000,margin:'0 auto',paddingBottom:40}}>
      <div className="fu">
        <h2 className="syne" style={{fontSize:24,fontWeight:800,marginBottom:4}}>👥 {lang==='ru'?'Команда разработчиков':'Development Team'}</h2>
        <p style={{fontSize:13,color:'var(--muted)'}}>{lang==='ru'?'Люди которые делают osu!Nya':'The people building osu!Nya'}</p>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
        {devs.map((dev,i) => (
          <div key={dev.username} className={`card fu d${i+1}`} style={{padding:24,background:'linear-gradient(135deg,var(--card) 0%,var(--card2) 100%)',position:'relative',overflow:'hidden',borderRadius:20}}>
            <div style={{position:'absolute',right:-40,top:-40,width:160,height:160,borderRadius:'50%',background:'radial-gradient(circle,var(--glow) 0%,transparent 70%)',pointerEvents:'none',opacity:0.5}}/>
            <div style={{display:'flex',alignItems:'center',gap:16,position:'relative'}}>
              <div style={{width:70,height:70,borderRadius:20,flexShrink:0,background:`url(${dev.avatar}) center/cover`,boxShadow:'0 8px 24px rgba(0,0,0,0.2)',border:'2px solid var(--border)'}}/>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4}}>
                  <span className="syne" style={{fontSize:18,fontWeight:800}}>{dev.username}</span>
                  <span style={{fontSize:16}}>{dev.flag}</span>
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:`${dev.badgeColor}20`,color:dev.badgeColor,border:`1px solid ${dev.badgeColor}40`}}>{dev.badge}</span>
                </div>
                <div style={{fontSize:12,color:'var(--muted)',marginBottom:12}}>{dev.role}</div>
                {dev.twitchUrl ? (
                  <a href={dev.twitchUrl} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:20,fontSize:11,fontWeight:700,background:'rgba(145,71,255,0.15)',border:'1px solid rgba(145,71,255,0.35)',color:'#9147ff',textDecoration:'none'}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
                    Twitch
                  </a>
                ) : (
                  <a href={dev.osuUrl} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:20,fontSize:11,fontWeight:700,background:'var(--dim)',border:'1px solid var(--border)',color:'var(--a)',textDecoration:'none'}}>
                    <svg width="12" height="12" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="16" r="16" opacity="0.3"/><circle cx="16" cy="16" r="7"/></svg>
                    osu! Profile
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{padding:'16px 24px',borderRadius:16,background:'rgba(255,255,255,0.03)',border:'1px dashed var(--border)',textAlign:'center'}}>
        <span style={{fontSize:14,fontWeight:600,color:'var(--muted)'}}>
          <span style={{color:'var(--a)'}}>osu!Nya</span> — {lang==='ru'?'сервис, сделанный игроками для игроков. Идеи, предложения, контакт — gashuser@proton.me':'a service made by players for players. Ideas, suggestions, contact — gashuser@proton.me'}
        </span>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:16}}>
        <div className="card fu" style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
          <h3 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:4}}>💎 {lang==='ru'?'Прямая поддержка':'Direct Support'}</h3>
          <a href="https://boosty.to/osunya" target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:12,padding:16,borderRadius:12,background:'linear-gradient(90deg,#f05a22,#ff8235)',textDecoration:'none',color:'#fff',transition:'transform 0.2s'}}
            onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'} onMouseLeave={e=>e.currentTarget.style.transform='none'}>
            <span style={{fontSize:24}}>🚀</span>
            <div><div style={{fontWeight:800,fontSize:15}}>Boosty</div><div style={{fontSize:11,opacity:0.9}}>{lang==='ru'?'Карты РФ, СНГ, подписки':'Cards & Subscriptions'}</div></div>
          </a>
          <a href="https://t.me/send?start=IV2ZAhdU5j1M" target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:12,padding:16,borderRadius:12,background:'rgba(0,136,204,0.15)',border:'1px solid rgba(0,136,204,0.3)',textDecoration:'none',color:'#0088cc',transition:'0.2s'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(0,136,204,0.22)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(0,136,204,0.15)'}>
            <span style={{fontSize:24}}>🤖</span>
            <div><div style={{fontWeight:800,fontSize:15}}>CryptoBot</div><div style={{fontSize:11,opacity:0.9}}>{lang==='ru'?'Донат через Telegram':'Via Telegram'}</div></div>
          </a>
        </div>

        <div className="card fu" style={{padding:24}}>
          <h3 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:12}}>⚡ {lang==='ru'?'Криптовалюта':'Crypto Wallets'}</h3>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {crypto.map(coin => (
              <div key={coin.name} style={{background:'var(--dim)',padding:'10px 12px',borderRadius:10,border:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:9,fontWeight:800,color:'var(--a)',textTransform:'uppercase'}}>{coin.name}</div>
                  <div style={{fontSize:11,fontFamily:'monospace',color:'var(--muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:2}}>{coin.address}</div>
                </div>
                <button onClick={()=>copyToClipboard(coin.address,coin.name)} style={{padding:'6px 10px',borderRadius:6,background:copied===coin.name?'rgba(68,255,68,0.12)':'var(--bg2)',border:'1px solid var(--border)',color:copied===coin.name?'#44ff44':'var(--muted)',fontSize:10,fontWeight:700,cursor:'pointer',flexShrink:0,marginLeft:10}}>
                  {copied===coin.name ? (lang==='ru'?'ГОТОВО ✓':'COPIED ✓') : (lang==='ru'?'КОПИ':'COPY')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN SIDEBAR
══════════════════════════════════════════ */
const Sidebar = ({ page, setPage, open, petals, setPetals }) => {
  const { t, lang }    = useLang();
  const navItems       = getNavItems(t, lang);
  const [showSettings, setShowSettings] = useState(false);
  const settingsBtnRef = useRef(null);

  const NavItem = ({ n }) => {
    const isActive = page === n.id;
    return (
      <div onClick={() => setPage(n.id)} title={n.label} style={{
        width:56, display:'flex', flexDirection:'column', alignItems:'center',
        gap:3, padding:'9px 4px', borderRadius:14, cursor:'pointer',
        transition:'all 0.18s',
        background: isActive ? 'var(--dim)' : 'transparent',
        border:`1px solid ${isActive ? 'var(--border)' : 'transparent'}`,
        color: isActive ? 'var(--a)' : 'var(--muted)',
        position:'relative',
      }}
      onMouseEnter={e=>{ if(!isActive){ e.currentTarget.style.background='var(--dim)'; e.currentTarget.style.color='var(--a3)'; } }}
      onMouseLeave={e=>{ if(!isActive){ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--muted)'; } }}
      >
        {isActive && <div style={{position:'absolute',left:0,top:'50%',transform:'translateY(-50%)',width:3,height:14,borderRadius:'0 3px 3px 0',background:'var(--a)',boxShadow:'0 0 8px var(--glow)'}}/>}
        <span style={{fontSize:17,lineHeight:1}}>{n.icon}</span>
        <span style={{fontSize:9,fontWeight:700,letterSpacing:0.3,lineHeight:1,textAlign:'center'}}>{n.label}</span>
      </div>
    );
  };

  return (
    <>
      <aside className={`sidebar ${open ? 'open' : ''}`} style={{
        width:74, minHeight:'100vh',
        background:'var(--bg2)', borderRight:'1px solid var(--border)',
        padding:'10px 0 10px', display:'flex', flexDirection:'column',
        alignItems:'center', gap:2,
        position:'sticky', top:0, flexShrink:0, zIndex:200,
      }}>
        <div style={{width:48,height:48,marginBottom:14,marginTop:4,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'50%',boxShadow:'0 0 10px rgba(255,255,255,0.6)'}}>
          <img src="/logo.png" alt="logo" style={{width:'100%',height:'100%',objectFit:'contain',filter:'drop-shadow(0 0 4px rgba(255,255,255,0.4))'}}/>
        </div>

        {navItems.map(n => <NavItem key={n.id} n={n} />)}

        <div style={{marginTop:'auto',paddingBottom:4}}>
          <div ref={settingsBtnRef} onClick={()=>setShowSettings(v=>!v)} title={lang==='ru'?'Настройки':'Settings'} style={{
            width:56, display:'flex', flexDirection:'column', alignItems:'center',
            gap:3, padding:'9px 4px', borderRadius:14, cursor:'pointer', transition:'all 0.18s',
            background: showSettings ? 'var(--dim)' : 'transparent',
            border:`1px solid ${showSettings ? 'var(--border)' : 'transparent'}`,
            color: showSettings ? 'var(--a)' : 'var(--muted)',
          }}
          onMouseEnter={e=>{ if(!showSettings){ e.currentTarget.style.background='var(--dim)'; e.currentTarget.style.color='var(--a3)'; } }}
          onMouseLeave={e=>{ if(!showSettings){ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--muted)'; } }}
          >
            <span style={{fontSize:17,lineHeight:1}}>⚙</span>
            <span style={{fontSize:9,fontWeight:700,letterSpacing:0.3}}>{lang==='ru'?'Настр.':'Settings'}</span>
          </div>
        </div>
      </aside>

      {showSettings && <SettingsPanel onClose={()=>setShowSettings(false)} anchorRef={settingsBtnRef} petals={petals} setPetals={setPetals} />}
    </>
  );
};

export default Sidebar;